import { useState, useEffect } from 'react'
import { LiveTracking } from '@/types'
import { 
  Activity, 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Clock,
  AlertCircle
} from 'lucide-react'
import { getLiveTracking, createLiveTracking, updateLiveTracking, deleteLiveTracking } from '../lib/api'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

interface LiveTrackingProps {
  strategyId: string
}

const LiveTrackingComponent: React.FC<LiveTrackingProps> = ({ strategyId }) => {
  const [liveTracking, setLiveTracking] = useState<LiveTracking[]>([])
  const [editingTracking, setEditingTracking] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    platform: '',
    tracking_period_start: '',
    tracking_period_end: '',
    actual_return: '',
    actual_drawdown: '',
    actual_trade_count: '',
    notes: ''
  })

  useEffect(() => {
    fetchLiveTracking()
  }, [strategyId])

  const fetchLiveTracking = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getLiveTracking(strategyId)
      setLiveTracking(data)
    } catch (error) {
      console.error('获取实盘追踪数据失败:', error)
      setError('获取实盘追踪数据失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const trackingData = {
        strategy_id: strategyId,
        platform: formData.platform,
        tracking_period_start: formData.tracking_period_start,
        tracking_period_end: formData.tracking_period_end || null,
        actual_return: parseFloat(formData.actual_return) || null,
        actual_drawdown: parseFloat(formData.actual_drawdown) || null,
        actual_trade_count: parseInt(formData.actual_trade_count) || null,
        notes: formData.notes
      }
      
      if (editingTracking) {
        await updateLiveTracking(editingTracking, trackingData)
      } else {
        await createLiveTracking(trackingData)
      }
      
      // 重新获取数据
      await fetchLiveTracking()
      
      setEditingTracking(null)
      setShowAddForm(false)
      setFormData({
        platform: '',
        tracking_period_start: '',
        tracking_period_end: '',
        actual_return: '',
        actual_drawdown: '',
        actual_trade_count: '',
        notes: ''
      })
    } catch (error) {
      console.error('保存实盘追踪失败:', error)
      setError('保存实盘追踪失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingTracking(null)
    setShowAddForm(false)
    setFormData({
      platform: '',
      tracking_period_start: '',
      tracking_period_end: '',
      actual_return: '',
      actual_drawdown: '',
      actual_trade_count: '',
      notes: ''
    })
  }

  const handleDelete = async (trackingId: string) => {
    if (!confirm('确定要删除这条追踪记录吗？')) return
    
    try {
      setLoading(true)
      setError(null)
      await deleteLiveTracking(trackingId)
      await fetchLiveTracking()
    } catch (error) {
      console.error('删除实盘追踪失败:', error)
      setError('删除实盘追踪失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'completed':
        return 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
      case 'paused':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return '进行中'
      case 'completed':
        return '已完成'
      case 'paused':
        return '已暂停'
      default:
        return '未知'
    }
  }

  // 对比数据 - 从实际数据生成
  const comparisonData = liveTracking.length > 0 ? liveTracking.map(tracking => ({
    date: tracking.tracking_period_start,
    backtest: 0, // 这里应该从回测数据获取
    live: tracking.actual_return || 0
  })) : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gradient">实盘追踪</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary"
          disabled={loading}
        >
          <Plus className="w-4 h-4 mr-2" />
          添加追踪
        </button>
      </div>

      {/* 错误显示 */}
      {error && (
        <div className="card bg-red-500/10 border border-red-500/30">
          <div className="flex items-center text-red-400">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* 加载状态 */}
      {loading && (
        <div className="card">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-400"></div>
            <span className="ml-3 text-white/70">加载中...</span>
          </div>
        </div>
      )}

      {/* 追踪列表 */}
      {!loading && (
        <div className="space-y-4">
          {liveTracking.map((tracking) => (
            <div key={tracking.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{tracking.platform}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tracking.status || 'active')}`}>
                      {getStatusLabel(tracking.status || 'active')}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-white/60 mb-3">
                    <Clock className="w-4 h-4 mr-1" />
                    {new Date(tracking.tracking_period_start).toLocaleDateString('zh-CN')} - 
                    {tracking.tracking_period_end ? new Date(tracking.tracking_period_end).toLocaleDateString('zh-CN') : '进行中'}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-sm text-white/70">实际收益</span>
                      <p className="text-lg font-semibold text-white">{tracking.actual_return}%</p>
                    </div>
                    <div>
                      <span className="text-sm text-white/70">实际回撤</span>
                      <p className="text-lg font-semibold text-white">{tracking.actual_drawdown}%</p>
                    </div>
                    <div>
                      <span className="text-sm text-white/70">交易次数</span>
                      <p className="text-lg font-semibold text-white">{tracking.actual_trade_count}</p>
                    </div>
                  </div>
                  {tracking.notes && (
                    <div className="mt-3 text-sm text-white/80">
                      {tracking.notes}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => setEditingTracking(tracking.id)}
                    className="text-violet-400 hover:text-violet-300"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(tracking.id)}
                    className="text-red-400 hover:text-red-300"
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 对比图表 */}
      {liveTracking.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">回测 vs 实盘对比</h3>
          <div className="bg-white/5 rounded-xl p-4">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                <XAxis dataKey="date" stroke="#ffffff80" />
                <YAxis stroke="#ffffff80" />
                <Tooltip 
                  formatter={(value: any) => [value.toLocaleString(), '资金']}
                  labelFormatter={(label) => `日期: ${label}`}
                  contentStyle={{
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="backtest" 
                  stroke="#8b5cf6" 
                  strokeWidth={3}
                  name="回测结果"
                  dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="live" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  name="实盘结果"
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 添加/编辑表单 */}
      {(showAddForm || editingTracking) && (
        <div className="card">
          <TrackingEditForm
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={handleCancel}
            loading={loading}
          />
        </div>
      )}

      {liveTracking.length === 0 && !showAddForm && (
        <div className="empty-state">
          <Activity className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">暂无实盘追踪</h3>
          <p className="text-white/50 mb-6">开始追踪策略的实盘表现</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            添加第一条追踪
          </button>
        </div>
      )}
    </div>
  )
}

interface TrackingEditFormProps {
  formData: any
  setFormData: (data: any) => void
  onSave: () => void
  onCancel: () => void
  loading?: boolean
}

const TrackingEditForm: React.FC<TrackingEditFormProps> = ({
  formData,
  setFormData,
  onSave,
  onCancel,
  loading = false
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">追踪信息</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">交易平台</label>
          <input
            type="text"
            value={formData.platform}
            onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
            className="w-full"
            placeholder="例如: Binance"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">开始日期</label>
          <input
            type="date"
            value={formData.tracking_period_start}
            onChange={(e) => setFormData({ ...formData, tracking_period_start: e.target.value })}
            className="w-full"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">结束日期</label>
          <input
            type="date"
            value={formData.tracking_period_end}
            onChange={(e) => setFormData({ ...formData, tracking_period_end: e.target.value })}
            className="w-full"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">实际收益率 (%)</label>
          <input
            type="number"
            step="0.01"
            value={formData.actual_return}
            onChange={(e) => setFormData({ ...formData, actual_return: e.target.value })}
            className="w-full"
            placeholder="例如: 15.5"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">实际回撤 (%)</label>
          <input
            type="number"
            step="0.01"
            value={formData.actual_drawdown}
            onChange={(e) => setFormData({ ...formData, actual_drawdown: e.target.value })}
            className="w-full"
            placeholder="例如: -8.2"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">交易次数</label>
          <input
            type="number"
            value={formData.actual_trade_count}
            onChange={(e) => setFormData({ ...formData, actual_trade_count: e.target.value })}
            className="w-full"
            placeholder="例如: 45"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-white mb-2">备注</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="w-full"
          rows={3}
          placeholder="追踪过程中的观察和发现..."
        />
      </div>
      
      <div className="flex justify-end space-x-3">
        <button onClick={onCancel} className="btn-secondary" disabled={loading}>
          <X className="w-4 h-4 mr-2" />
          取消
        </button>
        <button onClick={onSave} className="btn-primary" disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          {loading ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  )
}

export default LiveTrackingComponent 